import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserCircle2 } from "lucide-react";

const MAX_LEN = 600;

export function ProfileEditDialog({ open, onOpenChange, targetUser, onSaved }) {
  const [text, setText] = useState(targetUser?.profile || "");
  const [busy, setBusy] = useState(false);

  // Synka in fältet när targetUser byts (Radix Dialog håller mounten kvar).
  useEffect(() => {
    setText(targetUser?.profile || "");
  }, [targetUser?.id, targetUser?.profile]);

  const save = async () => {
    if (!targetUser?.id) return;
    setBusy(true);
    try {
      const value = (text || "").trim().slice(0, MAX_LEN);
      const { data } = await api.put(`/users/${targetUser.id}/profile`, {
        profile: value || null,
      });
      toast.success("Profil sparad");
      onSaved?.(data?.profile ?? null);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara profilen");
    } finally {
      setBusy(false);
    }
  };

  const remaining = MAX_LEN - (text || "").length;
  const firstName = (targetUser?.name || "").split(" ")[0] || "användaren";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCircle2 size={18} className="text-[color:var(--moss)]" />
            Profil — {firstName}
          </DialogTitle>
          <DialogDescription>
            Kort beskrivning som Edit använder som kontext (ålder, vanor,
            allergier, intressen). Max {MAX_LEN} tecken.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 mt-1">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            placeholder={`T.ex. "${firstName} är 13 år, går i 7:an, spelar fotboll i DVIF P12. Tränar tisdag och torsdag 17:30."`}
            rows={6}
            className="resize-none text-sm"
            data-testid="profile-edit-textarea"
            autoFocus
          />
          <div className="flex items-center justify-between text-[11px] text-[color:var(--text-tertiary)]">
            <span>Synlig för Edit och familjen.</span>
            <span
              className={remaining < 0 ? "text-red-600" : remaining < 50 ? "text-amber-600" : ""}
              data-testid="profile-edit-remaining"
            >
              {remaining} tecken kvar
            </span>
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="profile-edit-cancel"
          >
            Avbryt
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
            data-testid="profile-edit-save"
          >
            {busy ? "Sparar…" : "Spara"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
